/**
 * React Query hooks for configuration data (environments, services, artifacts, files).
 * Provides cached access to project-scoped configuration entities that are
 * shared across multiple deploy tabs, eliminating redundant API calls.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';

import type {
    Artifact,
    ChangedDatabase,
    ConfigurationFile,
    DatabaseDefinition,
    DatabaseEnvironmentConfig,
    EnvironmentSummary,
    Server,
    ServiceDefinition,
    ServiceInstance
} from '@/api/configuration';
import {
    listArtifacts,
    listChangedDatabases,
    listConfigFiles,
    listDatabaseDefinitions,
    listDatabaseEnvironmentConfigs,
    listDatabaseScripts,
    listEnvironments,
    listServers,
    listServiceDefinitions,
    listServiceInstances
} from '@/api/configuration';
import { INFREQUENT_STALE_TIME } from '@/modules/query/query-client';
import { queryKeys } from '@/modules/query/query-keys';

/**
 * Fetches and caches environments for a project.
 * Environments rarely change, so uses a longer stale time.
 */
export function useEnvironments(projectKey: string) {
  return useQuery<EnvironmentSummary[]>({
    queryKey: queryKeys.configuration.environments(projectKey),
    queryFn: () => listEnvironments(projectKey),
    enabled: !!projectKey,
    staleTime: INFREQUENT_STALE_TIME,
    retry: 1,
  });
}

/**
 * Fetches and caches service definitions for a project.
 */
export function useServiceDefinitions(projectKey: string) {
  return useQuery<ServiceDefinition[]>({
    queryKey: queryKeys.configuration.serviceDefinitions(projectKey),
    // Sort alphabetically by display name so every consumer (services list,
    // config file form, deploy override dropdowns) renders a consistent order.
    queryFn: () =>
      listServiceDefinitions(projectKey).then((defs) =>
        [...defs].sort((a, b) => a.name.localeCompare(b.name))
      ),
    enabled: !!projectKey,
    staleTime: INFREQUENT_STALE_TIME,
    retry: 1,
  });
}

/**
 * Fetches and caches servers for a specific environment.
 */
export function useServers(projectKey: string, environmentId: string) {
  return useQuery<Server[]>({
    queryKey: queryKeys.configuration.servers(projectKey, environmentId),
    queryFn: () => listServers(projectKey, environmentId),
    enabled: !!projectKey && !!environmentId,
    retry: 1,
  });
}

/**
 * Fetches and caches service instances for a specific environment.
 */
export function useServiceInstances(projectKey: string, environmentId: string) {
  return useQuery<ServiceInstance[]>({
    queryKey: queryKeys.configuration.serviceInstances(projectKey, environmentId),
    queryFn: () => listServiceInstances(projectKey, environmentId),
    enabled: !!projectKey && !!environmentId,
    retry: 1,
  });
}

/**
 * Fetches and caches artifacts for a project.
 */
export function useArtifacts(projectKey: string) {
  return useQuery<Artifact[]>({
    queryKey: queryKeys.configuration.artifacts(projectKey),
    queryFn: () => listArtifacts(projectKey),
    enabled: !!projectKey,
    staleTime: INFREQUENT_STALE_TIME,
    retry: 1,
  });
}

/**
 * Fetches and caches configuration files for a project.
 */
export function useConfigFiles(projectKey: string) {
  return useQuery<ConfigurationFile[]>({
    queryKey: queryKeys.configuration.configFiles(projectKey),
    queryFn: () => listConfigFiles(projectKey),
    enabled: !!projectKey,
    staleTime: INFREQUENT_STALE_TIME,
    retry: 1,
  });
}

/**
 * Hook to get cache invalidation helpers for configuration data.
 * Call these after mutations (create, update, delete) to refresh cached data.
 */
export function useInvalidateConfiguration(projectKey: string) {
  const queryClient = useQueryClient();

  return {
    invalidateEnvironments: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.configuration.environments(projectKey) }),
    invalidateServiceDefinitions: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.configuration.serviceDefinitions(projectKey) }),
    invalidateServers: (environmentId: string) =>
      queryClient.invalidateQueries({ queryKey: queryKeys.configuration.servers(projectKey, environmentId) }),
    invalidateServiceInstances: (environmentId: string) =>
      queryClient.invalidateQueries({ queryKey: queryKeys.configuration.serviceInstances(projectKey, environmentId) }),
    invalidateArtifacts: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.configuration.artifacts(projectKey) }),
    invalidateConfigFiles: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.configuration.configFiles(projectKey) }),
    invalidateDatabaseDefinitions: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.configuration.databaseDefinitions(projectKey) }),
    invalidateDatabaseEnvironmentConfigs: (databaseId: string) =>
      queryClient.invalidateQueries({ queryKey: queryKeys.configuration.databaseEnvironmentConfigs(projectKey, databaseId) }),
  };
}

/**
 * Fetches and caches database definitions for a project.
 */
export function useDatabaseDefinitions(projectKey: string) {
  return useQuery<DatabaseDefinition[]>({
    queryKey: queryKeys.configuration.databaseDefinitions(projectKey),
    queryFn: () => listDatabaseDefinitions(projectKey),
    enabled: !!projectKey,
    staleTime: INFREQUENT_STALE_TIME,
    retry: 1,
  });
}

/**
 * Fetches and caches database environment configs for a specific database definition.
 */
export function useDatabaseEnvironmentConfigs(projectKey: string, databaseId: string) {
  return useQuery<DatabaseEnvironmentConfig[]>({
    queryKey: queryKeys.configuration.databaseEnvironmentConfigs(projectKey, databaseId),
    queryFn: () => listDatabaseEnvironmentConfigs(projectKey, databaseId),
    enabled: !!projectKey && !!databaseId,
    retry: 1,
  });
}

/**
 * Fetches and caches SQL scripts for a specific database definition.
 * Only enabled when both projectKey and databaseId are provided.
 */
export function useDatabaseScripts(projectKey: string, databaseId: string) {
  return useQuery<string[]>({
    queryKey: queryKeys.configuration.databaseScripts(projectKey, databaseId),
    queryFn: () => listDatabaseScripts(projectKey, databaseId),
    enabled: !!projectKey && !!databaseId,
    retry: 1,
  });
}


/**
 * Fetches and caches execution history (changed databases) for a specific database and environment.
 * Only enabled when projectKey, databaseId, and environmentId are all provided.
 */
export function useChangedDatabases(projectKey: string, databaseId: string, environmentId: string) {
  return useQuery<ChangedDatabase[]>({
    queryKey: queryKeys.configuration.changedDatabases(projectKey, databaseId, environmentId),
    queryFn: () => listChangedDatabases(projectKey, databaseId, environmentId),
    enabled: !!projectKey && !!databaseId && !!environmentId,
    retry: 1,
  });
}
